import React, { useEffect } from 'react';
import { cn } from './cn';

/**
 * A dialog that is a bottom sheet on phones and a centred panel from sm up.
 *
 * The shape change is the point. On a handset a centred dialog fights the
 * keyboard and puts its actions out of thumb reach, so below sm this anchors
 * to the bottom edge, pads past the home indicator, and (see SheetFooter)
 * stacks its buttons in reverse so the primary action sits lowest.
 *
 * Also locks the page behind it: without that, scrolling a sheet that has hit
 * its end chains to the document and drags the page underneath, which is the
 * most obvious "this is a website" tell on iOS.
 */
export const Sheet = ({ open = true, onClose, label, wide, className, children }) => {
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (e) => e.key === 'Escape' && onClose?.();
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-scrim sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={label}
      onClick={onClose}
    >
      <div
        className={cn(
          'flex w-full flex-col overflow-hidden bg-surface-overlay border border-line',
          'rounded-t-panel border-b-0 shadow-sheet pb-[var(--safe-bottom)]',
          'max-h-[calc(100dvh-var(--safe-top)-1.5rem)]',
          'sm:rounded-panel sm:border-b sm:shadow-float sm:pb-0 sm:max-h-[min(80dvh,720px)]',
          wide ? 'sm:max-w-[900px]' : 'sm:max-w-[560px]',
          className
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag affordance — meaningless once this is a centred dialog. */}
        <div className="mx-auto mt-2.5 mb-0.5 h-1 w-9 shrink-0 rounded-pill bg-line-loud sm:hidden" />
        {children}
      </div>
    </div>
  );
};

export const SheetHeader = ({ className, ...props }) => (
  <div
    className={cn(
      'flex shrink-0 items-center justify-between gap-3 border-b border-line px-5 py-4',
      className
    )}
    {...props}
  />
);

export const SheetBody = ({ className, ...props }) => (
  <div
    className={cn('min-h-0 flex-1 overflow-y-auto overscroll-contain p-5', className)}
    style={{ WebkitOverflowScrolling: 'touch' }}
    {...props}
  />
);

/**
 * Sticky action row. `flex-col-reverse` on phones puts the primary action —
 * which callers pass last — at the bottom, nearest the thumb.
 */
export const SheetFooter = ({ className, ...props }) => (
  <div
    className={cn(
      'flex shrink-0 flex-col-reverse gap-2 border-t border-line bg-surface-overlay px-5 py-4',
      'sm:flex-row sm:justify-end',
      '[&>button]:w-full sm:[&>button]:w-auto',
      className
    )}
    {...props}
  />
);

export default Sheet;
